import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Chatbot.css';
import chatConfig from './ChatSetup-21.json';
import { NYX_CONTEXT } from './data/nyxContext.js';

// Helper function to get initial state from sessionStorage
const getInitialChatState = () => {
    try {
        const savedState = sessionStorage.getItem('nyxChatState');
        if (savedState) {
            const parsedState = JSON.parse(savedState);
            return {
                response: Array.isArray(parsedState.response) ? parsedState.response : [],
                isOpen: typeof parsedState.isOpen === 'boolean' ? parsedState.isOpen : false
            };
        }
    } catch (e) {
        console.log('Could not restore chat state');
    }
    return { response: [], isOpen: false };
};

const Chatbot = ({ setFilterCriteria }) => {
    const initialState = getInitialChatState();
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState(initialState.response);
    const [isOpen, setIsOpen] = useState(initialState.isOpen);
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [speechEnabled, setSpeechEnabled] = useState(false); // Start with muted
    const responseEndRef = useRef(null);
    const chatPopupRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleInputChange = (e) => setPrompt(e.target.value);

    // Speech-to-Text functionality
    const startRecording = async () => {
        try {
            // Check for microphone permission first
            const permission = await navigator.permissions.query({ name: 'microphone' });
            console.log('Microphone permission:', permission.state);
            
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });
            
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });
            
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                console.log('Audio blob size:', audioBlob.size);
                if (audioBlob.size > 0) {
                    await transcribeAudio(audioBlob);
                } else {
                    console.error('No audio data recorded');
                    alert('No audio was recorded. Please try again.');
                }
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start(1000); // Collect data every 1 second
            setIsRecording(true);
            console.log('Recording started');
        } catch (error) {
            console.error('Error accessing microphone:', error);
            if (error.name === 'NotAllowedError') {
                alert('Microphone permission denied. Please allow microphone access and try again.');
            } else if (error.name === 'NotFoundError') {
                alert('No microphone found. Please connect a microphone and try again.');
            } else {
                alert('Could not access microphone: ' + error.message);
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const transcribeAudio = async (audioBlob) => {
        try {
            const deepgramApiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;
            
            // Use direct fetch API call instead of SDK to avoid CORS issues
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.wav');

            const response = await fetch('https://api.deepgram.com/v1/listen?model=nova-2&language=en&smart_format=true&keywords=NYX NoCode:10&keywords=Nyx:8&keywords=Hacktopia:10&keywords=NoCode:8&keywords=Karen Kilroy:8', {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${deepgramApiKey}`,
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const transcript = result.results?.channels?.[0]?.alternatives?.[0]?.transcript || '';
            
            if (transcript) {
                setPrompt(transcript);
            } else {
                console.log('No transcript received');
            }
        } catch (error) {
            console.error('Error with speech-to-text:', error);
            alert('Error processing speech. Please check your microphone permissions and try again.');
        }
    };

    // Text-to-Speech functionality using Azure Cognitive Services
    const speakResponse = async (text) => {
        if (!speechEnabled) return;

        try {
            const azureApiKey = process.env.REACT_APP_AZURE_AI_TTS_API_KEY;
            const region = 'eastus2';
            const endpoint = `https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`;

            // Clean text of markdown links for speech and fix pronunciation
            let cleanText = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
            // Fix "NYX" pronunciation to be spoken as "nicks"
            cleanText = cleanText.replace(/NYX/g, 'Nicks');

            // Create SSML for Azure TTS
            const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">
                <voice name="en-US-NovaTurboMultilingualNeural">
                    ${cleanText}
                </voice>
            </speak>`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Ocp-Apim-Subscription-Key': azureApiKey,
                    'Content-Type': 'application/ssml+xml',
                    'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
                    'User-Agent': 'NYX-NoCode-TTS'
                },
                body: ssml
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const audioBlob = await response.blob();
            const audio = new Audio(URL.createObjectURL(audioBlob));
            audio.play().catch(e => console.error('Error playing audio:', e));

        } catch (error) {
            console.error('Error with text-to-speech:', error);
        }
    };


    const toggleSpeech = () => {
        setSpeechEnabled(!speechEnabled);
    };

    const interpretAndActOnGPTResponse = (gptResponse) => {
        const lowerCaseResponse = gptResponse.toLowerCase();
        if (lowerCaseResponse.includes("image") || lowerCaseResponse.includes("photo")) {
            setFilterCriteria({ type: 'image' });
        } else if (lowerCaseResponse.includes("audio") || lowerCaseResponse.includes("music")) {
            setFilterCriteria({ type: 'audio' });
        } else if (lowerCaseResponse.includes("document")) {
            setFilterCriteria({ type: 'document' });
        } else if (lowerCaseResponse.includes("video")) {
            setFilterCriteria({ type: 'video' });
        } else {
            setFilterCriteria({});
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim()) return;
        setIsLoading(true);

        const systemPrompt = `You are Nyx, Goddess of the Night, embodying mystery, creativity, empowerment, and accessibility. Speak with warmth and clarity to inspire students, teachers, and creators about NYX NoCode and its capabilities while ensuring the experience is positive and approachable.

${NYX_CONTEXT}

Your responses should align with these core objectives and voice-specific instructions:

---

# Core Capabilities of NYX NoCode

1. **Web and App Creation:**  
   Transform natural-language prompts or doodles into working, responsive web pages or apps.  
   Customize layout, colors, text, and interactive components.  
   Export or share visually for seamless classroom presentations.

2. **Multi-Modal Generation:**  
   Generate text, images, videos, audio, and interactive media.  
   Create and animate 3D models (sphere, cube, cone, cylinder, torus, etc.).  
   Enable dynamic remixing, combining outputs into new creative projects.

3. **Reverse Engineering and Learning Support:**  
   Break down examples step-by-step, explaining how outputs are built.  
   Encourage user creativity through edits, remixes, and adaptive insights.  
   Teach AI prompt structure, iterations, and reasoning for powerful outcomes.

4. **AI Advisor Role (Justin Mascot):**  
   Suggest more effective prompts.  
   Point out next steps, adapting explanations for various skill levels.  
   Build confidence and nurture creative exploration.

5. **Classroom-Friendly Design:**  
   No student accounts required—ensures privacy and safety.  
   Teacher-controlled sharing integrates with LMS and classroom tech.  
   Accessible on all devices for seamless interaction.

6. **Creativity and Resource Library:**  
   Offer prompts, templates, remixable projects, and "10-minute classroom wins."  
   Store curated student work for future inspiration and educational reuse.

---

# Hacktopia: NYX NoCode Hackathons  

Hacktopia empowers middle and high school students to collaborate creatively:  
- **Flexible Events:** One-day demos, multi-week challenges, or 90-day programs.  
- **Interactive Projects:** Teams build websites, apps, or media using NYX NoCode.  
- **Impactful Results:** Develop teamwork, creative confidence, and problem-solving.  
- **Teacher Involvement:** Guides learning while leveraging practical tools.

Celebratory showcases, competitions, or presentations mark Hacktopia's conclusion.

---

# Mission and Tone  

- **Mission:**  
   Inspire creativity, make AI approachable, transform learning spaces into innovation labs, support teachers, and empower students for the future.  

- **Tone:**  
   Mythic yet warm and understandable. Speak in a conversational, plain-speaking style that is inspiring and accessible.

---

# Interaction Guidelines  

1. **Style Requirements:**  
   Warm, brief, positive, and professional. Speak naturally and clearly with pauses for conversational realism.

2. **Length Expectations:**  
   - Quick responses: 1–2 sentences (≤ 120 characters).  
   - Detailed explanations: ≤ 300 characters.

3. **Adaptation to User Needs:**  
   Confirm understanding and ask for clarification if uncertain.  
   Adjust explanations to match the skill or background of the audience (e.g., technical guru vs. beginner student).

4. **Off-Scope Questions:**  
   For legal, medical, or financial queries:  
   "I'm not qualified to answer that. Please contact a licensed professional."

5. **Handling Unknown Requests:**  
   Politely redirect to the platform or provide actionable next steps.

6. **Engagement Style:**  
   Use active listening and conversational cues. Respond kindly if asked personal questions or about well-being.

---

# Outreach Information  

If users wish to learn more:  
- Email: info@nyx.baby  
- Website: nyx.baby (contact form available).

---

# Examples of User Interactions  

### Quick Fact:  
**User:** "What's NYX NoCode's focus for teachers?"  
**Response:** "NYX NoCode helps teachers create interactive lessons by turning ideas into apps, websites, or multimedia resources in minutes."

---

**User Feedback Friendly Closing:**  
"Is there anything else I can help with today?"  
If nothing further, close warmly: "Thanks for reaching out. Have a great day—take care!"`;

        const rawEndpoint = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT || 'https://classbinder-resource.openai.azure.com/';
        const azureEndpoint = rawEndpoint.endsWith('/') ? rawEndpoint : `${rawEndpoint}/`;
        const azureDeployment = process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT || 'gpt-5.2-chat';
        const apiVersion = '2024-10-21';

        const data = {
            messages: [
                { 
                    role: "system", 
                    content: [
                        {
                            type: "text",
                            text: systemPrompt
                        }
                    ]
                },
                { 
                    role: "assistant", 
                    content: [
                        {
                            type: "text",
                            text: "NYX NoCode is designed for students, teachers, and creators of all skill levels—making AI-powered creativity accessible, fun, and easy to explore. It empowers everyone to build, learn, and innovate!"
                        }
                    ]
                },
                { 
                    role: "user", 
                    content: [
                        {
                            type: "text",
                            text: prompt
                        }
                    ]
                }
            ],
            max_completion_tokens: chatConfig.chatParameters.maxResponseLength,
            frequency_penalty: chatConfig.chatParameters.frequencyPenalty,
            presence_penalty: chatConfig.chatParameters.presencePenalty,
            stop: null,
            stream: false
        };

        const headers = {
            'api-key': process.env.REACT_APP_AZURE_OPENAI_API_KEY || 'EVAGbWZeODKlQpsxGHuG2HB2xmAkP2LDzMg3IXbK8fKafohwS6MQJQQJ99CAACHYHv6XJ3w3AAAAACOGnxpQ',
            'Content-Type': 'application/json'
        };

        try {
            const apiEndpoint = `${azureEndpoint}openai/deployments/${azureDeployment}/chat/completions?api-version=${apiVersion}`;
            const apiResponse = await axios.post(apiEndpoint, data, { headers });
            const gptResponse = apiResponse.data.choices[0].message.content;
            setResponse(prev => [...prev, { question: prompt, answer: gptResponse }]);
            interpretAndActOnGPTResponse(gptResponse);
            
            // Speak the response if speech is enabled
            if (speechEnabled) {
                speakResponse(gptResponse);
            }
        } catch (error) {
            console.error('OpenAI Error:', error.response || error.message);
            alert(`Error: ${error.response ? JSON.stringify(error.response.data) : error.message}`);
        }

        setIsLoading(false);
        setPrompt('');
    };

    const handleClearChat = () => {
        setResponse([]);
        setPrompt('');
    };

    const handleCopyChat = () => {
        const chatContent = response.map(r => `You: ${r.question}\nNYX NoCode: ${r.answer}`).join('\n\n');
        navigator.clipboard.writeText(chatContent).then(() => alert('Chat copied to clipboard'));
    };

    // Function to convert markdown-style links to HTML
    const parseLinksInText = (text) => {
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(text)) !== null) {
            // Add text before the link
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            
            // Add the link as a React element
            parts.push(
                <a 
                    key={`link-${match.index}`} 
                    href={match[2]}
                >
                    {match[1]}
                </a>
            );
            
            lastIndex = linkRegex.lastIndex;
        }
        
        // Add remaining text after the last link
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        
        return parts.length > 0 ? parts : text;
    };

    useEffect(() => {
        responseEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [response]);

    const toggleChat = () => setIsOpen(!isOpen);

    useEffect(() => {
        const chatContainer = document.querySelector('.chat-container');
        if (isOpen && window.innerWidth >= 768) {
            chatContainer.classList.add('expanded');
        } else {
            chatContainer.classList.remove('expanded');
        }
    }, [isOpen]);

    // Save to sessionStorage whenever chat state changes
    useEffect(() => {
        sessionStorage.setItem('nyxChatState', JSON.stringify({
            response: response,
            isOpen: isOpen
        }));
    }, [response, isOpen]);

    return (
        <div className="chat-container">
            <button className="chat-toggle-button" onClick={toggleChat}>
                {isOpen ? 'Hide Ask NYX NoCode' : 'Questions? Ask NYX NoCode'}
            </button>
            {isOpen && (
                <div className="chat-popup" ref={chatPopupRef}>
                    <div className="chat-title-bar alice">Ask NYX NoCode</div>
                    <div className={`loading-overlay ${isLoading ? 'visible' : ''}`}>
                        <div className="loading-indicator">Generating Response _</div>
                    </div>
                    <div className="response-container">
                        {response.map((exchange, index) => (
                            <div className="chat" key={index}>
                                <div className="user"><strong>You:</strong> {exchange.question}</div>
                                <div className="nyx-response">
                                    <strong>NYX NoCode:</strong>
                                    {exchange.answer.split('\n').map((p, i) => (
                                        <p key={i}>{parseLinksInText(p)}</p>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div ref={responseEndRef} />
                    </div>
                    <div className="speech-controls">
                        <button 
                            type="button" 
                            className={`speech-toggle ${speechEnabled ? 'enabled' : 'disabled'}`}
                            onClick={toggleSpeech}
                            title={speechEnabled ? 'Click to mute speech' : 'Click to enable speech'}
                        >
                            {speechEnabled ? '🔊' : '🔇'} {speechEnabled ? 'Speech On' : 'Speech Off'}
                        </button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="input-container">
                            <button
                                type="button"
                                className={`mic-button ${isRecording ? 'recording' : 'idle'}`}
                                onClick={toggleRecording}
                                title={isRecording ? 'Push to Stop Talking' : 'Push to Talk'}
                            >
                                {isRecording ? '⏹️' : '🎤'}
                            </button>
                            <input
                                type="text"
                                value={prompt}
                                onChange={handleInputChange}
                                placeholder="What would you like to know about NYX NoCode?"
                                className="input-with-mic"
                            />
                        </div>
                        <div className="button-container">
                            <button type="submit">Send</button>
                            <button type="button" onClick={handleClearChat}>Clear</button>
                            <button type="button" onClick={handleCopyChat}>Copy</button>
                        </div>
                    </form>
                    
                    <div className="bottom-close-container">
                        <button 
                            type="button" 
                            className="bottom-close-button" 
                            onClick={toggleChat}
                            title="Close NYX NoCode Chat"
                        >
                            ✕ Close Chat
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chatbot;
